import React, { useCallback, useMemo, useState } from 'react';
import DataTable from './DataTable';
import ContainerBox from './ContainerBox';
import FileUpload from './FileUpload';
import Modal from './Modal';
import ConnectSourceModal from './warehouse/ConnectSourceModal';
import './DataUploadContainer.css';

// In-app uploads are parsed and processed on the webserver, so this stays well below
// the API's 500MB proxy limit (the SDK streams data through without materializing it).
const MAX_SIZE_BYTES = 500 * 1024 * 1024; // 500MB, matches UPLOAD_MAX_BYTES
const ACCEPTED_EXTENSIONS = ['.csv', '.xls', '.xlsx', '.json', '.zip'];

function getFileExt(name) {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
}

function isAcceptedFile(file) {
  const ext = getFileExt(file.name);
  return ACCEPTED_EXTENSIONS.includes(ext);
}


const DataUploadContainer = ({
  analysis,
  isNewAnalysis,
  modelStatusChangeCounter = 0,
  ensureAnalysisExists, // async () => { id, ... }
  onDatasetCreated, // (dataset) => void
  onReadyClick, // () => void
  onColumnsChanged, // (dropColumns: string[]) => void - called when columns are dropped/undropped
  onUserContextChanged, // () => void - called when user context is saved
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // { columns: [], rows: [] }
  // eslint-disable-next-line no-unused-vars -- setter used for drop-column state
  const [columnsToDrop, setColumnsToDrop] = useState(new Set());
  const [userContext, setUserContext] = useState('');
  const [originalUserContext, setOriginalUserContext] = useState('');
  const [uploadInstruction, setUploadInstruction] = useState('');
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectModalMsg, setRejectModalMsg] = useState('');
  const [recomputeModalOpen, setRecomputeModalOpen] = useState(false);
  const [recomputeModalMsg, setRecomputeModalMsg] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Multi-file preprocessing state
  const [rawUploads, setRawUploads] = useState([]);
  const [warehouseOpen, setWarehouseOpen] = useState(false);
  const [preprocessingStatus, setPreprocessingStatus] = useState(null);
  const [isPreprocessing, setIsPreprocessing] = useState(false);
  const [preprocessingError, setPreprocessingError] = useState(null);
  const [showPreprocessingDetails, setShowPreprocessingDetails] = useState(false);
  const [isAnalysisLocked, setIsAnalysisLocked] = useState(false);
  const [preprocessingMode, setPreprocessingMode] = useState(
    analysis?.preprocessing_mode || 'deterministic'
  );

  // Sync the toggle when the parent's analysis object changes (e.g. on first load).
  React.useEffect(() => {
    if (analysis?.preprocessing_mode) {
      setPreprocessingMode(analysis.preprocessing_mode);
    }
  }, [analysis?.preprocessing_mode]);

  // Optimistic toggle: update local state immediately, persist via PATCH, revert on failure.
  const handlePreprocessingModeChange = useCallback(async (newMode) => {
    if (newMode === preprocessingMode) return;
    const previous = preprocessingMode;
    setPreprocessingMode(newMode);
    if (!analysis?.id) return;
    try {
      const { apiService } = await import('../services/api');
      await apiService.updateAnalysis(analysis.id, { preprocessing_mode: newMode });
    } catch (e) {
      setPreprocessingMode(previous);
    }
  }, [preprocessingMode, analysis?.id]);

  const refreshAnalysisLockState = useCallback(async () => {
    if (!analysis?.id) {
      setIsAnalysisLocked(false);
      return;
    }
    try {
      const { apiService } = await import('../services/api');
      const artifactsResp = await apiService.getArtifacts(analysis.id);
      const artifacts = artifactsResp?.data?.artifacts || [];
      const hasTrainedOrStaleModel = artifacts.some(
        (art) => art.page === 'full_model' && art.kind === 'full_model' && (art.status === 'ready' || art.status === 'stale')
      );
      setIsAnalysisLocked(Boolean(hasTrainedOrStaleModel));
    } catch (e) {
      setIsAnalysisLocked(false);
    }
  }, [analysis?.id]);

  const isReady = useMemo(() => !!preview && !uploading && !isPreprocessing, [preview, uploading, isPreprocessing]);

  // Warehouse import landed as a raw Dataset — refresh the raw-uploads list
  // so it shows up next to the uploaded files, ready for preprocessing.
  const handleWarehouseImported = useCallback(async () => {
    setWarehouseOpen(false);
    try {
      const { apiService } = await import('../services/api');
      const statusResp = await apiService.getPreprocessingStatus(analysis?.id);
      setRawUploads(statusResp.data.raw_uploads || []);
    } catch (e) {
      // best-effort — the dataset exists server-side either way
    }
  }, [analysis?.id]);

  // Load user context from analysis object
  React.useEffect(() => {
    if (analysis?.user_context) {
      setUserContext(analysis.user_context);
      setOriginalUserContext(analysis.user_context);
    }
  }, [analysis?.user_context]);

  // Load upload instruction from artifacts
  React.useEffect(() => {
    const loadInstruction = async () => {
      if (!analysis?.id) return;
      try {
        const { apiService } = await import('../services/api');
        const artifactsResp = await apiService.getArtifacts(analysis.id);
        const uploadArtifact = artifactsResp?.data?.artifacts?.find(
          art => art.page === 'upload' && art.kind === 'upload_instruction' && art.status === 'ready'
        );
        if (uploadArtifact?.payload?.instruction) {
          setUploadInstruction(uploadArtifact.payload.instruction);
        }
      } catch (e) {
        // best-effort
      }
    };
    loadInstruction();
  }, [analysis?.id]);

  // Load preview from backend if existing dataset(s)
  React.useEffect(() => {
    const load = async () => {
      try {
        if (!analysis?.datasets || analysis.datasets.length === 0) {
          setUploadedFiles([]);
          return;
        }
        const first = analysis.datasets[0];
        if (!first?.id) return;
        const { apiService } = await import('../services/api');
        const [previewResp, userContextResp, filesResp] = await Promise.all([
          apiService.getDatasetPreview(first.id),
          apiService.getUserContext(analysis.id),
          apiService.getUploadedFiles(first.id, 'raw'),
        ]);
        if (previewResp?.data?.preview) setPreview(previewResp.data.preview);
        if (userContextResp?.data?.hasOwnProperty('user_context')) {
          const contextValue = userContextResp.data.user_context || '';
          setUserContext(contextValue);
          setOriginalUserContext(contextValue);
        }
        if (filesResp?.data?.files) {
          setUploadedFiles(filesResp.data.files);
        }
      } catch (e) { /* best-effort */ }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- analysis.id would trigger reload on id change; datasets is the stable trigger
  }, [analysis?.datasets]);
  
  // Load raw uploads and preprocessing status
  React.useEffect(() => {
    const loadPreprocessingState = async () => {
      if (!analysis?.id) return;
      try {
        const { apiService } = await import('../services/api');
        const statusResp = await apiService.getPreprocessingStatus(analysis.id);
        if (statusResp?.data) {
          setRawUploads(statusResp.data.raw_uploads || []);
          setPreprocessingStatus(statusResp.data.session);
        }
      } catch (e) {
        // best-effort
      }
    };
    loadPreprocessingState();
  }, [analysis?.id]);

  React.useEffect(() => {
    refreshAnalysisLockState();
  }, [refreshAnalysisLockState, analysis?.datasets, modelStatusChangeCounter]);

  const maybeShowSimilarityRejectModal = React.useCallback((errorLike) => {
    const payload =
      (errorLike?.response && typeof errorLike.response.data === 'object' && errorLike.response.data)
      || (errorLike?.data && typeof errorLike.data === 'object' && errorLike.data)
      || (typeof errorLike === 'object' ? errorLike : {});

    const statusCode = errorLike?.response?.status;
    const detailRaw = payload?.detail;
    const detail =
      typeof detailRaw === 'string'
        ? detailRaw
        : Array.isArray(detailRaw)
          ? detailRaw.join(' ')
          : (detailRaw && typeof detailRaw === 'object' && typeof detailRaw.message === 'string')
            ? detailRaw.message
            : 'The uploaded dataset must be similar to your trained analysis dataset.';

    const overlapRatio = payload?.overlap_ratio;
    const requiredOverlapRatio = payload?.required_overlap_ratio;
    const errorType = payload?.error_type;
    const hasOverlapFields = Array.isArray(payload?.missing_columns) || Array.isArray(payload?.extra_columns);
    const hasSimilarityHints = /overlap|similar|retrain|column/i.test(JSON.stringify(payload || {}));

    const looksLikeSimilarityError =
      errorType === 'dataset_similarity'
      || typeof overlapRatio === 'number'
      || hasOverlapFields
      || /overlap|similar/i.test(detail || '')
      || hasSimilarityHints
      || (isAnalysisLocked && statusCode === 400);

    if (!looksLikeSimilarityError) return false;

    // Backend message already includes overlap information, so use it as-is
    setRejectModalMsg(detail);
    setRejectModalOpen(true);
    return true;
  }, [isAnalysisLocked]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0) {
      await processFiles(files);
    }
  };

  const handleFileSelect = async (filesOrFile) => {
    const files = Array.isArray(filesOrFile) ? filesOrFile : [filesOrFile];
    await processFiles(files);
  };

  const processFiles = useCallback(async (files) => {
    setError(null);
    setPreprocessingError(null);

    if (isAnalysisLocked && files.length > 1) {
      setError('After model training, upload a single replacement file only.');
      return;
    }
    
    // Validate all files
    for (const file of files) {
    if (!isAcceptedFile(file)) {
        setError(`Unsupported file type: ${file.name}. Please upload .csv, .xls, .xlsx, .json or .zip files.`);
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
        setError(`File too large: ${file.name}. Please upload files smaller than 500MB.`);
      return;
      }
    }

    setUploading(true);
    try {
      // Create analysis upfront if needed
      let analysisId = analysis?.id;
      if (isNewAnalysis || !analysisId) {
        const created = await ensureAnalysisExists();
        analysisId = created.id;
      }

      const { apiService } = await import('../services/api');
      
      // Upload files as raw uploads
      const uploadPromises = files.map(async (file) => {
      const form = new FormData();
      form.append('file', file);
        return apiService.uploadRawFile(analysisId, form);
      });
      
      await Promise.all(uploadPromises);
      
      // Refresh raw uploads list
      const statusResp = await apiService.getPreprocessingStatus(analysisId);
      if (statusResp?.data) {
        setRawUploads(statusResp.data.raw_uploads || []);
        setPreprocessingStatus(statusResp.data.session);
      }
      
      // When analysis is locked (model trained), auto-trigger preprocessing
      // so the overlap check runs on the final preprocessed file immediately.
      if (isAnalysisLocked) {
        setUploading(false);
        await runPreprocessingWorkflow(analysisId);
        return;
      }
      
    } catch (err) {
      console.error(err);
      // Refresh raw uploads so UI reflects what actually persisted
      try {
        const currentAnalysisId = analysis?.id;
        if (currentAnalysisId) {
          const { apiService: svc } = await import('../services/api');
          const statusResp = await svc.getPreprocessingStatus(currentAnalysisId);
          if (statusResp?.data) {
            setRawUploads(statusResp.data.raw_uploads || []);
            setPreprocessingStatus(statusResp.data.session);
          }
        }
      } catch (_) { /* best-effort */ }
      const shownInModal = maybeShowSimilarityRejectModal(err);
      if (!shownInModal) {
        const errorData = err?.response?.data;
        const detail = errorData?.detail;
        if (detail) {
          setError(detail);
        } else {
          setError('Failed to upload file(s).');
        }
      } else {
        setError(null);
      }
    } finally {
      setUploading(false);
    }
  }, [analysis?.id, isNewAnalysis, ensureAnalysisExists, isAnalysisLocked, maybeShowSimilarityRejectModal]);
  
  const runPreprocessingWorkflow = async (analysisId) => {
    setIsPreprocessing(true);
    setPreprocessingError(null);
    
    try {
      const { apiService: api } = await import('../services/api');
      const result = await api.runPreprocessing(analysisId || analysis?.id);
      
      if (result?.data?.status === 'complete') {
        // Refresh the analysis data
        const [analysisResp, statusResp] = await Promise.all([
          api.getAnalysis(analysisId || analysis?.id),
          api.getPreprocessingStatus(analysisId || analysis?.id)
        ]);
        
        setPreprocessingStatus(statusResp?.data?.session);
        setRawUploads(statusResp?.data?.raw_uploads || []);
        
        // Load the new dataset preview
        const datasetId = result?.data?.dataset_id;
        if (datasetId) {
          const [previewResp, filesResp] = await Promise.all([
            api.getDatasetPreview(datasetId),
            api.getUploadedFiles(datasetId, 'raw'),
          ]);
          
          if (previewResp?.data?.preview) {
            setPreview(previewResp.data.preview);
          }
          if (filesResp?.data?.files) {
            setUploadedFiles(filesResp.data.files);
          }
          
          // Notify parent
          if (analysisResp?.data?.datasets?.[0]) {
            onDatasetCreated?.(analysisResp.data.datasets[0]);
          }

          await refreshAnalysisLockState();
        }

        const recomputeSummary = result?.data?.recomputation_summary;
        if (recomputeSummary && recomputeSummary?.is_locked) {
          const autoPred = recomputeSummary.auto_predicted_targets || [];
          const staleTargets = recomputeSummary.stale_targets || [];

          const autoPredMsg = autoPred.length > 0
            ? `Auto-predicted ${autoPred.map(t => `${t.target_column} (${t.n_predictions})`).join(', ')}.`
            : 'No auto-predictions were generated.';
          const staleMsg = staleTargets.length > 0
            ? `Marked stale for retraining: ${staleTargets.map(t => t.target_column).join(', ')}.`
            : 'No targets were marked stale.';

          const backendMsg = recomputeSummary?.message ? `${recomputeSummary.message} ` : '';

          setRecomputeModalMsg(`${backendMsg}${autoPredMsg} ${staleMsg} You can recompute sections anytime using the refresh buttons.`.trim());
          setRecomputeModalOpen(true);
        }
      } else if (result?.data?.status === 'error') {
        // Refresh raw uploads (backend may have cleaned them up)
        try {
          const statusResp = await api.getPreprocessingStatus(analysisId || analysis?.id);
          if (statusResp?.data) {
            setRawUploads(statusResp.data.raw_uploads || []);
            setPreprocessingStatus(statusResp.data.session);
          }
        } catch (_) { /* best-effort */ }
        const shownInModal = maybeShowSimilarityRejectModal(result);
        if (!shownInModal) {
          const detail = result?.data?.detail || result?.data?.error || 'Dataset was rejected during preprocessing.';
          setRejectModalMsg(detail);
          setRejectModalOpen(true);
          setPreprocessingError(null);
        }
      }
    } catch (err) {
      console.error('Preprocessing error:', err);
      // Refresh raw uploads (backend may have cleaned them up)
      try {
        const { apiService: svc2 } = await import('../services/api');
        const statusResp = await svc2.getPreprocessingStatus(analysisId || analysis?.id);
        if (statusResp?.data) {
          setRawUploads(statusResp.data.raw_uploads || []);
          setPreprocessingStatus(statusResp.data.session);
        }
      } catch (_) { /* best-effort */ }
      const shownInModal = maybeShowSimilarityRejectModal(err);
      if (!shownInModal) {
        const errorData = err?.response?.data;
        setRejectModalMsg(errorData?.detail || errorData?.error || 'Dataset was rejected during preprocessing.');
        setRejectModalOpen(true);
        setPreprocessingError(null);
      } else {
        setPreprocessingError(null);
      }
    } finally {
      setIsPreprocessing(false);
    }
  };

  // eslint-disable-next-line no-unused-vars -- retained for potential local UI toggles
  const toggleDropColumn = async (col) => {
    setColumnsToDrop((prev) => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col); else next.add(col);
      return next;
    });
  };

  const handleRemoveFile = async (fileId) => {
    if (isAnalysisLocked) {
      setError('Deleting files is disabled after model training. Please replace the file instead.');
      return;
    }

    try {
      const { apiService } = await import('../services/api');
      
      // Check if it's a raw upload or a final dataset
      const isRawUpload = rawUploads.some(u => u.id === fileId);
      
      if (isRawUpload) {
        await apiService.deleteRawUpload(fileId);
        setRawUploads(prev => prev.filter(u => u.id !== fileId));
        
        // If no more raw uploads, clear everything
        if (rawUploads.length <= 1) {
          setPreview(null);
          setColumnsToDrop(new Set());
          setUploadedFiles([]);
          setPreprocessingStatus(null);
        }
      } else {
      await apiService.deleteDataset(fileId);
      setPreview(null);
      setColumnsToDrop(new Set());
      setUploadedFiles([]);
      onDatasetCreated?.(null);
      }
    } catch (err) {
      console.error('Failed to remove file:', err);
      setError('Failed to remove file.');
    }
  };

  const handleReplaceFile = () => {
    // Trigger file input click - same as clicking the dropzone
    // The file upload will automatically replace the existing dataset
  };

  const saveUserContext = async () => {
    try {
      if (analysis?.id) {
        const { apiService } = await import('../services/api');
        await apiService.setUserContext(analysis.id, userContext);
        setOriginalUserContext(userContext);
        // Notify parent that user context has changed
        if (onUserContextChanged) {
          onUserContextChanged();
        }
      }
    } catch (e) { /* ignore */ }
  };

  const handleUserContextChange = (e) => {
    const value = e.target.value;
    if (value.length <= 1000) {
      setUserContext(value);
    }
  };

  const hasUserContextChanges = userContext !== originalUserContext;
  
  // Calculate combined file list for display
  const displayFiles = useMemo(() => {
    // If we have a final dataset, show that
    if (uploadedFiles.length > 0) {
      return uploadedFiles;
    }
    // Otherwise show raw uploads
    return rawUploads.map(u => ({
      id: u.id,
      name: u.original_filename,
      extension: u.file_extension,
      uploaded_at: u.created_at,
      rows: u.rows_count,
      cols: u.columns_count,
      status: u.status
    }));
  }, [uploadedFiles, rawUploads]);
  
  // Show preprocess button when there are raw uploads and no final dataset yet
  const showPreprocessButton = rawUploads.length > 0 && !preview && !isPreprocessing;

  return (
    <ContainerBox
      title="Upload Relevant Data"
      isReady={isReady}
      readyLabel="Data Analysis"
      onReadyClick={onReadyClick}
    >
      <div className='dataset-user-context'>
        <h3 className='container-box__section-title__h3'>Your Goal & Business Context</h3>
        <div className='dataset-user-context-field-description'>The goal you would like to achieve using predictive AI as well as context about the data and your business.</div>
        <textarea
          id='dataset-user-context-input'
          className='dataset-user-context__textarea'
          value={userContext}
          onChange={handleUserContextChange}
          placeholder='E.g. this is a customer database of a cookie subscription service and I would like to predict churn propensities.'
          maxLength={1000}
        />
        <div className='dataset-user-context__footer'>
          <span className='dataset-user-context__counter'>
            {userContext.length}/1000 characters
          </span>
          <button
            className={`btn ${hasUserContextChanges ? 'btn-primary' : 'btn-disabled'}`}
            onMouseDown={saveUserContext} // Using onMouseDown instead of onClick because onClick would only be triggered if the mouse button is clicked AND released,
            // which doesnt happen on the button because it slides up with the smaller text field upon clicking outside the text field.
            disabled={!hasUserContextChanges}
          >
            Save
          </button>
        </div>
      </div>

      {/* Display upload instruction */}
      {(uploadInstruction) && (
        <div className='data-upload-instructions'>
          <h4>Instruction</h4>
          <p>Fantastic! In order to train a predictive AI, we need a training dataset, which should have at least 100 rows.<br></br> {uploadInstruction} <br></br>Depending on how much raw data you have, it might suffice to annotate and upload only a subset of your data. However, the more data, the better!</p>
        </div>
      )}

      <div className='training-file-upload'>
        <h3>Training Data Upload</h3>
        <p className='upload-description'>
          Upload one or more CSV/Excel files. If you upload multiple files, they will be automatically analyzed, cleaned, and merged into a single training dataset.
        </p>
        <FileUpload
          onFileSelect={handleFileSelect}
          dragActive={dragActive}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          disabled={false}
          uploading={uploading}
          error={error}
          uploadedFiles={displayFiles}
          onRemoveFile={isAnalysisLocked ? null : handleRemoveFile}
          onReplaceFile={handleReplaceFile}
          multiFile={!isAnalysisLocked}
          uploadText={<>Drag and drop your files here, or <span className="upload-link">upload</span></>}
          uploadSubtext={isAnalysisLocked
            ? 'Supported formats: .csv, .xls, .xlsx, .json, .zip — Max 500MB — Model trained: replacement uploads only, delete disabled'
            : 'Supported formats: .csv, .xls, .xlsx, .json, .zip — Max 500MB each — Multiple files supported'}
        />

        {!isAnalysisLocked && (
          <button
            type="button"
            className="btn"
            style={{ marginTop: 10 }}
            onClick={() => setWarehouseOpen(true)}
          >
            Or connect a data warehouse (Snowflake · BigQuery · Databricks · Redshift)
          </button>
        )}
        <ConnectSourceModal
          open={warehouseOpen}
          onClose={() => setWarehouseOpen(false)}
          analysisId={analysis?.id}
          ensureAnalysisExists={async () => {
            const created = await ensureAnalysisExists();
            return created?.id || created;
          }}
          onImported={handleWarehouseImported}
        />

        {/* Preprocessing status */}
        {isPreprocessing && (
          <div className="preprocessing-status">
            <div className="preprocessing-spinner"></div>
            <span>{isAnalysisLocked ? 'Checking dataset compatibility...' : 'Processing and merging files...'}</span>
            {preprocessingStatus?.current_step && (
              <span className="preprocessing-step">{preprocessingStatus.current_step}</span>
            )}
          </div>
        )}
        
        {preprocessingError && (
          <div className="preprocessing-error">
            {preprocessingError}
          </div>
        )}
        
        {/* Show preprocessing button when files are uploaded */}
        {showPreprocessButton && (
          <div className="preprocessing-action">
            <p className="preprocessing-info">
              <strong>{rawUploads.length} file{rawUploads.length > 1 ? 's' : ''} uploaded.</strong>
              {rawUploads.length === 1 
                ? ' Click below to analyze and clean your data (handle missing values, remove duplicates, drop irrelevant columns).'
                : ' Click below to analyze, clean, and merge your files into a single training dataset.'
              }
            </p>
            <div className="preprocessing-files-list">
              {rawUploads.map((file, idx) => (
                <div key={file.id} className="preprocessing-file-item">
                  <span className="file-number">{idx + 1}.</span>
                  <span className="file-name">{file.original_filename}</span>
                  <span className="file-stats">{file.rows_count} rows × {file.columns_count} cols</span>
                </div>
              ))}
            </div>
            <div className="preprocessing-mode-toggle" style={{
              marginTop: 14, marginBottom: 14, padding: '10px 12px',
              border: '1px solid var(--line-soft, #e5e7eb)', borderRadius: 8,
              fontSize: 13,
            }}>
              <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 600, opacity: 0.85 }}>
                Preprocessing mode
              </div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 18, cursor: isPreprocessing ? 'not-allowed' : 'pointer' }}>
                <input
                  type="radio"
                  name="preprocessingMode"
                  value="deterministic"
                  checked={preprocessingMode === 'deterministic'}
                  onChange={() => handlePreprocessingModeChange('deterministic')}
                  disabled={isPreprocessing}
                />
                Standard pipeline
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: isPreprocessing ? 'not-allowed' : 'pointer' }}>
                <input
                  type="radio"
                  name="preprocessingMode"
                  value="rlm_only"
                  checked={preprocessingMode === 'rlm_only'}
                  onChange={() => handlePreprocessingModeChange('rlm_only')}
                  disabled={isPreprocessing}
                />
                AI agent (beta)
              </label>
              <div style={{ marginTop: 6, fontSize: 11, opacity: 0.7 }}>
                {preprocessingMode === 'rlm_only'
                  ? 'An RLM agent will inspect your raw files and write its own pandas to clean, merge, and feature-engineer. Slower, experimental, falls back to the standard pipeline if it fails.'
                  : 'The proven 6-stage pipeline with deterministic cleaning, merge planning, and time-series feature extraction.'}
              </div>
            </div>
            <button
              className="btn btn-primary preprocessing-btn"
              onClick={() => runPreprocessingWorkflow(analysis?.id)}
              disabled={isPreprocessing}
            >
              {rawUploads.length === 1 ? 'Clean & Prepare Data' : 'Process & Merge Files'}
            </button>
          </div>
        )}
        
        {/* Show preprocessing summary if available */}
        {preprocessingStatus?.status === 'complete' && preprocessingStatus?.summary && (
          <div className="preprocessing-summary">
            <div className="preprocessing-summary-header">
              <h4>Preprocessing Summary</h4>
            </div>
            <p>{preprocessingStatus.summary.summary || `Created dataset with ${preprocessingStatus.summary.n_rows} rows and ${preprocessingStatus.summary.n_cols} columns.`}</p>
            
            {/* Toggle button for actions - positioned below summary text */}
            {(preprocessingStatus.summary.actions_taken?.length > 0 || preprocessingStatus.summary.warnings?.length > 0) && (
              <button
                className={`preprocessing-toggle-details ${showPreprocessingDetails ? 'expanded' : ''}`}
                onClick={() => setShowPreprocessingDetails(!showPreprocessingDetails)}
                type="button"
              >
                <span className="toggle-arrow"></span>
                <span className="toggle-text">Show Actions Taken</span>
              </button>
            )}
            
            {/* Show cleaning actions taken - only when expanded */}
            {showPreprocessingDetails && preprocessingStatus.summary.actions_taken?.length > 0 && (
              <div className="preprocessing-actions">
                <strong>Actions taken:</strong>
                <ul>
                  {preprocessingStatus.summary.actions_taken
                    .filter(a => a.status === 'success')
                    .slice(0, 12)
                    .map((action, i) => {
                      // Skip entries without proper action field
                      // Note: column can be '_all_' for remove_duplicates, which is valid
                      if (!action.action) {
                        return null;
                      }
                      const sourceInfo = action.source_file ? ` [${action.source_file}]` : '';
                      return (
                        <li key={i}>
                          {action.action === 'fill_missing_median' && `Filled ${action.filled_count} missing values in "${action.column}" with median (${action.fill_value})${sourceInfo}`}
                          {action.action === 'fill_missing_mean' && `Filled ${action.filled_count} missing values in "${action.column}" with mean (${action.fill_value})${sourceInfo}`}
                          {action.action === 'fill_missing_mode' && `Filled ${action.filled_count} missing values in "${action.column}" with most common value${sourceInfo}`}
                          {action.action === 'drop_column' && `Dropped column "${action.column}"${action.missing_pct ? ` (${action.missing_pct}% missing)` : ''}${sourceInfo}`}
                          {action.action === 'remove_duplicates' && `Removed ${action.dropped_count} duplicate rows${sourceInfo}`}
                          {action.action === 'handle_outliers' && `Handled ${action.outlier_count} outliers in "${action.column}"${sourceInfo}`}
                          {action.action === 'standardize_values' && `Standardized values in "${action.column}"${sourceInfo}`}
                          {!['fill_missing_median', 'fill_missing_mean', 'fill_missing_mode', 'drop_column', 'remove_duplicates', 'handle_outliers', 'standardize_values'].includes(action.action) && 
                            `${action.action}${action.column ? ` on "${action.column}"` : ''}${sourceInfo}`}
                        </li>
                      );
                    })
                    .filter(Boolean)}
                  {preprocessingStatus.summary.actions_taken.filter(a => a.status === 'success').length > 12 && (
                    <li className="more-actions">...and {preprocessingStatus.summary.actions_taken.filter(a => a.status === 'success').length - 12} more actions</li>
                  )}
                </ul>
              </div>
            )}
            
            {/* Show warnings and PID notes - only when expanded */}
            {showPreprocessingDetails && (() => {
              const excludedColumns = preprocessingStatus.summary.excluded_columns || [];
              const pidNote = excludedColumns.length > 0
                ? `The following columns are kept in the dataset but excluded from modeling and analysis: ${excludedColumns.join(', ')}.`
                : null;
              const warningNotes = preprocessingStatus.summary.warnings || [];
              const allNotes = [
                ...(pidNote ? [pidNote] : []),
                ...warningNotes,
              ];
              if (allNotes.length === 0) return null;
              return (
                <div className="preprocessing-warnings">
                  <strong>Notes:</strong>
                  <ul>
                    {allNotes.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {preview && (
        <div className="preview">
          <h3 className="container-box__section-title__h3">Preview</h3>
          <DataTable
            columns={preview.columns}
            rows={preview.rows}
          />

        </div>
      )}

      <Modal
        isOpen={rejectModalOpen}
        onClose={() => { setRejectModalOpen(false); setRejectModalMsg(''); }}
        onConfirm={() => { setRejectModalOpen(false); setRejectModalMsg(''); }}
        title="Dataset Rejected"
        message={rejectModalMsg || 'The uploaded dataset was rejected because it is too different from the original dataset in this analysis'}
        confirmText="OK"
        cancelText="Close"
        isDangerous={false}
        informationLevel="error"
      />

      <Modal
        isOpen={recomputeModalOpen}
        onClose={() => { setRecomputeModalOpen(false); setRecomputeModalMsg(''); }}
        onConfirm={() => { setRecomputeModalOpen(false); setRecomputeModalMsg(''); }}
        title="Dataset Re-computation Summary"
        message={recomputeModalMsg || 'Dataset recomputation actions completed.'}
        confirmText="OK"
        cancelText="Close"
        isDangerous={false}
        informationLevel="info"
      />
    </ContainerBox>
  );
};

export default DataUploadContainer;
